import React, { useState } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import { showNotification } from "aldoo-ra/Notification"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { api } from "aldoo-ra/API"
import { getPlatform } from "aldoo-ra/PlatformDetector"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"

function NewsletterSubscribeSection() {
  const [email, setEmail] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const { t } = Localization()
  const texts = TextBundle({ id: "newsletter" })


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear any previous errors

    if (!email || !email.trim()) {
      setError(texts?.error_empty_email || 'Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(texts?.error_invalid_email || 'Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const newsletterAPI = api('newsletter/manage');

      const detectedPlatform = getPlatform();

      const response = await newsletterAPI({
        subscribe: {
          email: email.trim(),
          platform: detectedPlatform
        }
      });

      if (response.success) {
        // Show success notification
        showNotification({
          message: response.message || texts?.success_message || 'Successfully subscribed to newsletter!',
          className: "bg-primary text-white",
        });

        // Reset form
        setEmail('');
        setError('');
      } else {
        // Handle API error response
        setError(response.message || texts?.error_subscription_failed || 'Subscription failed. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);

      // Handle different types of errors
      if (error.message && error.message.includes('rate_limited')) {
        setError(texts?.error_rate_limited || 'Please wait a moment before trying again. Our system prevents too many rapid subscription attempts for security.');
      } else if (error.response?.data?.error === 'rate_limited') {
        setError(texts?.error_rate_limited || 'Please wait a moment before trying again. Our system prevents too many rapid subscription attempts for security.');
      } else {
        setError(texts?.error_network || 'Network error. Please check your connection and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Standalone render function for CMSRender
  const renderNewsletterContent = (data) => {
    return (
      <div className="relative flex flex-col w-full md:w-[700px] h-[410px] md:h-[480px] lg:h-[520px] mx-auto items-center justify-end lg:pb-[110px] mb-16 px-4 md:px-0">
        <img
          src={data.background_graphic}
          className="absolute w-full md:w-full max-w-[380px] md:max-w-full md:h-full object-contain bottom-12 md:-bottom-[100px] left-1/2 md:left-[20px] lg:left-[-60px] -translate-x-1/2 md:translate-x-0 -z-10 scale-[1] md:scale-[0.7] lg:scale-[1.9] xl:scale-[2.1]"
          alt="Background graphic"
        />

        {/* Content */}
        <div className="lg:h-auto w-full max-w-[460px] md:w-[560px] lg:max-w-[560px] flex flex-col items-center justify-between">
          <div className="flex flex-col items-center w-full">
            <Typography
              component="h2"
              variant="h1"
              className="text-center mb-6"
            >
              {data.title}
            </Typography>

            {/* Subtitle */}
            <Typography className="w-full text-center text-base lg:text-[20px] text-text leading-normal tracking-custom_1 font-normal mt-0 md:mt-4 lg:mt-0 mb-[56px] md:mb-8">
              {data.subtitle}
            </Typography>
          </div>

          {/* Subscribe Form */}
          <form
            onSubmit={handleSubmit}
            className=" w-full md:w-[500px] lg:w-[560px] xl:max-w-[582px] mt-[50px] lg:mt-0"
          >
            <div
              className={`h-[60px] flex items-center justify-between rounded-full pl-[13px] pr-2 md:mt-[80px] lg:mt-0 transition-colors duration-200 ${
                isFocused ? "bg-primary" : "bg-primary_lt"
              }`}
            >
              <div className="flex-1 flex items-center gap-3 px-2">
                <div className="flex-1 min-w-0">
                  {/* iOS-compatible input */}
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={data.placeholder}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      outline: "none",
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      border: "none",
                      background: "transparent",
                      fontSize: "16px",
                      padding: "8px 0",
                      margin: "0",
                      opacity: "1",
                      WebkitTextFillColor: isFocused ? "#ffffff" : "#000000",
                      color: isFocused ? "#ffffff" : "#000000",
                      WebkitBoxShadow: "0 0 0 1000px transparent inset",
                      boxShadow: "0 0 0 1000px transparent inset",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                    }}
                    className={`${
                      isFocused ? "placeholder-white" : "placeholder-primary"
                    }`}
                  />
                </div>
              </div>
              <Button
                type="submit"
                variant="primary_s"
                disabled={isSubmitting}
                className={`flex-shrink-0 min-w-[130px] mr-[5px] md:mr-0 transition-colors duration-200 ${
                  isFocused ? "bg-white !text-primary" : ""
                }`}
              >
                {isSubmitting ? "Subscribing..." : data.cta}
              </Button>
            </div>

            {/* Error message display */}
            {error && (
              <div className="mt-4 p-3 bg-error/10 border border-error rounded-[8px] transition-colors text-center">
                <Typography variant="body_16" className="text-error my-0">
                  {error}
                </Typography>
              </div>
            )}
          </form>
        </div>
      </div>
    )
  }

  return (
    <CMSRender
      contentType="NewsletterSubscribe"
      renderItem={renderNewsletterContent}
    />
  )
}

export const AldooComponent = {
  id: 'NewsletterSubscribeSection',
  component: NewsletterSubscribeSection,
  input: {},
  meta: {
    name: 'Newsletter Subscribe',
    description: 'Email subscription form with background graphic',
    category: 'Marketing',
  },
}

export default NewsletterSubscribeSection
